import type { KeyboardEvent } from 'react';

export const noNegativeNumberProps = {
  min: 0,
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === '-') e.preventDefault();
  },
};

/**
 * Props for a money field: text input so partial values like "12." survive typing
 * (a `type="number"` input reports those as empty and rejects cents unless `step` is set),
 * plus a decimal keypad on mobile. Pair with {@link sanitizeAmount} on change and
 * {@link formatAmountDisplay} on the value — those keep the value digits-and-one-dot,
 * so no negative can be entered.
 */
export const moneyInputProps = {
  type: 'text' as const,
  inputMode: 'decimal' as const,
};

/** Digits and at most one dot followed by up to 2 decimals. Raw value stays comma-free. */
export const sanitizeAmount = (value: string): string => {
  const cleaned = value.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  const decPart = cleaned
    .slice(firstDot + 1)
    .replace(/\./g, '')
    .slice(0, 2);
  return cleaned.slice(0, firstDot + 1) + decPart;
};

/** Group the integer part with commas, keeping whatever the user typed after the dot. */
export const formatAmountDisplay = (value: string | number): string => {
  const [intPart, decPart] = String(value).split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
};

/** A sanitized money string as a number rounded to cents; 0 for anything unparsable. */
export const parseAmount = (value: string | number): number => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

/**
 * A money draft as the canonical string the API stores — always with cents, and safe for
 * drafts still mid-typing (e.g. "12." → "12.00").
 */
export const toAmountString = (value: string | number): string => parseAmount(value).toFixed(2);

/** Empty is allowed (callers treat it as "no value"); anything else needs ≤ 2 decimals. */
export const amountInputError = (
  raw: string,
  message = 'Please enter an amount with up to 2 decimal places.',
): string | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  return /^\d+(\.\d{0,2})?$/.test(trimmed) ? undefined : message;
};
